import { useState } from "react";
import { rewrapPrivate } from "@timecapsule/crypto";
import { api, type PendingIn, type PendingOut } from "../api";
import { fingerprint } from "../keys";
import { keyFor } from "../keyring";
import { isKeyRequestCancelled, useSession } from "../session";
import { formatTime } from "../time";
import { errorText, useLang, useT } from "../i18n";
import { usePagedList } from "../pagedList";
import { ShowMore } from "../ShowMore";

/**
 * Capsules for someone who was not a member yet (PLAN.md 4.14, "sender approves").
 * Inbox shows what waits for this member's address; Sent shows what this member sent and who asks.
 */
export function PendingIncoming() {
  const t = useT();
  const { lang } = useLang();
  const { me } = useSession();
  const [failed, setError] = useState<string | null>(null);
  // Paged (PLAN.md 4.20): 20 at a time, newest first.
  const pages = usePagedList(`pending-in:${me.id}`, (cursor) => api.pendingIncoming(cursor));
  const list = pages.items ?? [];
  const load = pages.reload;
  const error = failed ?? pages.error;

  async function ask(p: PendingIn) {
    setError(null);
    try {
      await api.requestPending(p.id, lang);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (list.length === 0 && !error) return null;
  return (
    <section className="card pending-card">
      <h2>{t.pending.incomingTitle}</h2>
      <ul className="rows">
        {list.map((p) => (
          <li key={p.id}>
            <span className="grow">
              <b>{p.subject}</b> <span className="muted small">{t.pending.from(p.sender_name)}</span>
              {p.unlock_at && <span className="muted small"> · {t.capsules.opensAt(formatTime(p.unlock_at * 1000))}</span>}
              {p.sase && <span className="tag envelope-tag">{t.envelopes.asksReply}</span>}
            </span>
            <span className="small muted">
              {p.status === "waiting" && (me.pubkey ? t.pending.askText : t.pending.needKey)}
              {p.status === "requested" && t.pending.asked}
              {p.status === "declined" && t.pending.declined}
              {p.status === "expired" && t.pending.expired}
            </span>
            {p.status === "waiting" && me.pubkey && (
              <button className="primary small" onClick={() => void ask(p)}>
                {t.pending.ask}
              </button>
            )}
          </li>
        ))}
      </ul>
      <ShowMore hasMore={pages.hasMore} busy={pages.loadingMore} onMore={() => void pages.loadMore()} />
      {error && <p className="error small">{error}</p>}
    </section>
  );
}

/** `onDelivered`: the page reloads its list, so the copy shows as sent to the new member at once. */
export function PendingOutgoing({ onDelivered }: { onDelivered?: () => void }) {
  const t = useT();
  const { me, withKey, refresh } = useSession();
  const [failed, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Paged (PLAN.md 4.20): 20 at a time, newest first; delivered and declined ones stay in the history.
  const pages = usePagedList(`pending-out:${me.id}`, (cursor) => api.pendingOutgoing(cursor));
  const list = pages.items ?? [];
  const load = pages.reload;
  const error = failed ?? pages.error;

  /** Opens my copy with my key and wraps the same capsule to the key that asked. */
  async function approve(p: PendingOut) {
    if (!p.requested_pubkey || !confirm(t.pending.confirmApprove(p.requester_name ?? "?", p.email, fingerprint(p.requested_pubkey)))) return;
    setError(null);
    setBusy(p.id);
    try {
      const copy = await api.message(p.copy_id);
      // The copy is sealed to the key that was active when it was stored, perhaps an earlier one (PLAN.md 4.18 step 6).
      const copyKey = copy.event.tags.find((tag) => tag[0] === "p")?.[1] ?? null;
      const wrap = await withKey(t.pending.purpose, async (key) => rewrapPrivate(copy.event, await keyFor(copyKey, key), p.requested_pubkey!));
      await api.approvePending(p.id, wrap);
      void refresh().catch(() => undefined);
      await load();
      onDelivered?.();
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
    setBusy(null);
  }

  async function decline(p: PendingOut) {
    if (!confirm(t.pending.confirmDecline(p.email))) return;
    setError(null);
    try {
      await api.declinePending(p.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  }

  const open = list.filter((p) => p.status === "waiting" || p.status === "requested");
  if (open.length === 0 && !error) return null;
  return (
    <section className="card pending-card">
      <h2>{t.pending.outgoingTitle}</h2>
      <ul className="rows">
        {open.map((p) => (
          <li key={p.id}>
            <span className="grow">
              <b>{p.subject}</b> <span className="muted small">{t.pending.to(p.email)}</span>
            </span>
            {p.status === "waiting" && (
              <>
                <span className="small muted">{t.pending.waitingFor(formatTime(p.expires_at * 1000))}</span>
                <button className="link small" onClick={() => void decline(p)}>
                  {t.pending.cancel}
                </button>
              </>
            )}
            {p.status === "requested" && p.requested_pubkey && (
              <>
                <span className="small">{t.pending.requestedBy(p.requester_name ?? "?", p.email, fingerprint(p.requested_pubkey))}</span>
                <span className="chips">
                  <button className="primary small" disabled={busy === p.id} onClick={() => void approve(p)}>
                    {t.pending.approve}
                  </button>
                  <button className="small" onClick={() => void decline(p)}>
                    {t.pending.decline}
                  </button>
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="error small">{error}</p>}
    </section>
  );
}
